import * as api from "../clients/slashid-api/index"
import * as domain from "../domain/index"
import * as remote from "../utils/remote"

export class CrossDomainStorage {
    private readonly remote: HTMLIFrameElement | null
    private remoteReady: boolean = false

    public remoteOrigin: string | undefined

    constructor(iframe: HTMLIFrameElement | null, remoteOrigin: string | undefined) {
        this.remote = iframe
        this.remoteOrigin = remoteOrigin

        if (!this.remote) {
            // we are the remote => expect request messages
            window.addEventListener("message", this.processEvent.bind(this))
        }
    }

    private async getRemoteWindow(): Promise<Window> {
        const win = this.remote?.contentWindow
        if (!win) {
            throw new Error("this context has no remote")
        }

        if (this.remoteReady) {
            return win
        }

        await remote.ping(win)
        this.remoteReady = true
        return win
    }

    private async processEvent(event: MessageEvent<domain.RequestMessage>) {
        let sourceWindow: Window | null = null
        if (window.parent && event.source === window.parent) {
            sourceWindow = window.parent
        }
        if (!sourceWindow) {
            return
        }

        switch (event.data.type) {
            case domain.MessageType.StorageGetItem:
                {
                    const msg = event.data as domain.GetItemRequestMessage
                    try {
                        const value = await this.localGetItem(msg.key)
                        sourceWindow.postMessage(new domain.GetItemResponseMessage(event.data.id, value), "*")
                    } catch (e) {
                        sourceWindow.postMessage(new domain.GetItemResponseMessage(event.data.id, null, e), "*")
                    }
                }
                break

            case domain.MessageType.StorageSetItem:
                {
                    const msg = event.data as domain.SetItemRequestMessage
                    try {
                        await this.localSetItem(msg.key, msg.value)
                        sourceWindow.postMessage(new domain.SetItemResponseMessage(event.data.id), "*")
                    } catch (e) {
                        sourceWindow.postMessage(new domain.SetItemResponseMessage(event.data.id, e), "*")
                    }
                }
                break

            default:
                break
        }
    }

    public async localGetItem(key: string): Promise<string | null> {
        return window.localStorage.getItem(key)
    }

    public async remoteGetItem(key: string): Promise<string | null> {
        let remoteValue: string | null = null
        if (this.remote) {
            remoteValue = await new Promise<string | null>((resolve, reject) => {
                const request = new domain.GetItemRequestMessage(key)
                const responseListener = (event: MessageEvent<domain.GetItemResponseMessage>) => {
                    const response = event.data
                    if (response.id !== request.id) {
                        return
                    }

                    window.removeEventListener("message", responseListener)
                    if (response.error) {
                        reject(response.error)
                    } else {
                        resolve(response.value)
                    }
                }
                window.addEventListener("message", responseListener)

                this.getRemoteWindow().then((win) => {
                    win.postMessage(request, "*")
                })
            })
        }
        return remoteValue
    }

    public async localSetItem(key: string, value: string): Promise<void> {
        return window.localStorage.setItem(key, value)
    }

    public async remoteSetItem(key: string, value: string): Promise<void> {
        if (this.remote) {
            await new Promise<void>((resolve, reject) => {
                const request = new domain.SetItemRequestMessage(key, value)
                const responseListener = (event: MessageEvent<domain.SetItemResponseMessage>) => {
                    const response = event.data
                    if (response.id !== request.id) {
                        return
                    }

                    window.removeEventListener("message", responseListener)
                    if (response.error) {
                        reject(response.error)
                    } else {
                        resolve()
                    }
                }
                window.addEventListener("message", responseListener)

                this.getRemoteWindow().then((win) => {
                    win.postMessage(request, "*")
                })
            })
        }
    }
}

abstract class StorageBase {
    protected readonly store: CrossDomainStorage

    constructor(store: CrossDomainStorage) {
        this.store = store
    }
}

export class IdentifiersStorage extends StorageBase {
    private static LOCAL_STORAGE_IDENTIFIERS_KEY: string = "SLASHID_IDENTIFIERS"

    private stringify(identifiers: api.PersonHandle[]): string {
        return JSON.stringify(identifiers)
    }

    private parse(value: string | null): api.PersonHandle[] {
        if (!value) {
            return []
        }
        const identifiers = JSON.parse(value) as api.PersonHandle[]
        return identifiers
    }

    public async getAll(): Promise<api.PersonHandle[]> {
        const localIdentifiers: api.PersonHandle[] = this.parse(
            await this.store.localGetItem(IdentifiersStorage.LOCAL_STORAGE_IDENTIFIERS_KEY)
        )
        const remoteIdentifiers: api.PersonHandle[] = this.parse(
            await this.store.remoteGetItem(IdentifiersStorage.LOCAL_STORAGE_IDENTIFIERS_KEY)
        )
        return IdentifiersStorage.merge(localIdentifiers, remoteIdentifiers)
    }

    public async add(identifier: api.PersonHandle): Promise<void> {
        const identifiers = await this.getAll()
        const updatedIdentifiers = this.stringify(IdentifiersStorage.merge(identifiers, [identifier]))
        await this.store.localSetItem(IdentifiersStorage.LOCAL_STORAGE_IDENTIFIERS_KEY, updatedIdentifiers)
        await this.store.remoteSetItem(IdentifiersStorage.LOCAL_STORAGE_IDENTIFIERS_KEY, updatedIdentifiers)
    }

    private static identifiersEqual(id1: api.PersonHandle, id2: api.PersonHandle) {
        return id1.type === id2.type && id1.value === id2.value
    }

    private static merge(ids1: api.PersonHandle[], ids2: api.PersonHandle[]): api.PersonHandle[] {
        return ids1.concat(...ids2).reduce((acc: api.PersonHandle[], cur: api.PersonHandle) => {
            for (const id of acc) {
                if (IdentifiersStorage.identifiersEqual(id, cur)) {
                    return acc
                }
            }
            acc.push(cur)
            return acc
        }, []) as api.PersonHandle[]
    }
}

class CredentialsStorageBase extends StorageBase {
    protected static identifiersEqual(id1: string, id2: string) {
        return id1 === id2
    }

    protected static merge(ids1: string[], ids2: string[]): string[] {
        return ids1.concat(...ids2).reduce((acc: string[], cur: string) => {
            for (const id of acc) {
                if (CredentialsStorageBase.identifiersEqual(id, cur)) {
                    return acc
                }
            }
            acc.push(cur)
            return acc
        }, []) as string[]
    }
}

export class CredentialsStorage extends CredentialsStorageBase {
    private static LOCAL_STORAGE_WEBAUTHN_CREDENTIAL_ID_KEY: string = "SLASHID_WEBAUTHN_CREDENTIAL_ID"

    private stringify(credIds: string[]): string {
        return JSON.stringify(credIds)
    }

    private parse(value: string | null): string[] {
        if (!value) {
            return []
        }
        let parsed = null
        try {
            parsed = JSON.parse(value)
        } catch (e) {
            return [value]
        }
        if (!Array.isArray(parsed)) {
            return []
        }
        const credIds = parsed.filter((item) => typeof item === "string")
        return credIds as string[]
    }

    public async getAll(): Promise<string[]> {
        const localCredIds: string[] = this.parse(
            await this.store.localGetItem(CredentialsStorage.LOCAL_STORAGE_WEBAUTHN_CREDENTIAL_ID_KEY)
        )
        const remoteCredIds: string[] = this.parse(
            await this.store.remoteGetItem(CredentialsStorage.LOCAL_STORAGE_WEBAUTHN_CREDENTIAL_ID_KEY)
        )
        return CredentialsStorage.merge(localCredIds, remoteCredIds)
    }

    public async add(credId: string): Promise<void> {
        const credIds = await this.getAll()
        const updatedCredIds = this.stringify(CredentialsStorage.merge(credIds, [credId]))
        await this.store.localSetItem(CredentialsStorage.LOCAL_STORAGE_WEBAUTHN_CREDENTIAL_ID_KEY, updatedCredIds)
        await this.store.remoteSetItem(CredentialsStorage.LOCAL_STORAGE_WEBAUTHN_CREDENTIAL_ID_KEY, updatedCredIds)
    }
}

/**
 * @ignore
 *
 * Storage for local credentials. Contrary to the previous V1 storage, this class stores credentials IDs
 * by their scope, allowing clients to effectively register local credentials with more or less specific
 * scopes at their will. For the old default "global" credentials it falls back to an internal instance
 * of the V1 storage, so to keep older SDKs running seamlessly.
 */
export class CredentialsStorageV2 extends CredentialsStorageBase {
    private static LOCAL_STORAGE_WEBAUTHN_CREDENTIAL_ID_KEY_V2: string = "SLASHID_WEBAUTHN_CREDENTIAL_ID_V2"

    private readonly v1Storage: CredentialsStorage

    constructor(store: CrossDomainStorage) {
        super(store)
        this.v1Storage = new CredentialsStorage(store)
    }

    private stringify(credIdsMap: { [key: string]: string[] }): string {
        return JSON.stringify(credIdsMap)
    }

    private parse(value: string | null): { [key: string]: string[] } {
        if (!value) {
            return {}
        }

        let parsed = null
        try {
            parsed = JSON.parse(value)
        } catch (e) {
            return {}
        }

        if (typeof parsed !== "object") {
            return {}
        }

        const credIdsMap: { [key: string]: string[] } = {}
        for (const scope of Object.keys(parsed)) {
            if (typeof scope !== "string") {
                continue
            }
            if (!Array.isArray(parsed[scope])) {
                continue
            }

            const credIds = parsed[scope].filter((item: any): boolean => typeof item === "string")
            credIdsMap[scope] = credIds
        }
        return credIdsMap
    }

    private async getAll(): Promise<{ [key: string]: string[] }> {
        const localCredIdsMap: { [key: string]: string[] } = this.parse(
            await this.store.localGetItem(CredentialsStorageV2.LOCAL_STORAGE_WEBAUTHN_CREDENTIAL_ID_KEY_V2)
        )
        return localCredIdsMap
    }

    public async get(scope: string): Promise<string[]> {
        if (!this.store.remoteOrigin || this.store.remoteOrigin.endsWith(scope)) {
            return this.v1Storage.getAll()
        }

        const localCredIds = (await this.getAll())[scope] || []
        return localCredIds
    }

    public async add(scope: string, credId: string): Promise<void> {
        if (!this.store.remoteOrigin || this.store.remoteOrigin.endsWith(scope)) {
            this.v1Storage.add(credId)
            return
        }

        const localCredIdsMap = await this.getAll()
        const localCredIds = localCredIdsMap[scope] || []

        for (const id of localCredIds) {
            if (id === credId) {
                console.warn(`Attempted to add already present credential ID: scope=${scope} credId=${credId}`)
                return
            }
        }
        localCredIds.push(credId)
        localCredIdsMap[scope] = localCredIds

        const updatedCredIdsMap = this.stringify(localCredIdsMap)
        await this.store.localSetItem(
            CredentialsStorageV2.LOCAL_STORAGE_WEBAUTHN_CREDENTIAL_ID_KEY_V2,
            updatedCredIdsMap
        )
    }
}
